/**
 * AgentMessageBubble Component
 * Individual message bubble in the conversation
 */

import React, { useCallback } from 'react'
import type { AgentMessage, AgentAction, AgentType } from '../../types'
import { AGENT_TYPE_CONFIG } from './agentConfig'

interface AgentMessageBubbleProps {
  /**
   * The message to display
   */
  message: AgentMessage
  /**
   * Agent name for assistant messages
   */
  agentName?: string
  /**
   * Agent emoji/type for avatar
   */
  agentEmoji?: AgentType
  /**
   * Callback when an action button is clicked
   */
  onAction?: (action: AgentAction) => void
}

/**
 * Formats timestamp for display
 */
function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)
}

/**
 * Individual message bubble with role-based styling
 */
export const AgentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  message,
  agentName,
  agentEmoji,
  onAction,
}) => {
  const handleActionClick = useCallback(
    (action: AgentAction) => {
      onAction?.(action)
    },
    [onAction]
  )

  const emoji = agentEmoji ? AGENT_TYPE_CONFIG[agentEmoji]?.emoji : '🤖'

  // System messages - centered, subtle styling
  if (message.role === 'system') {
    return (
      <div className="mb-4 flex justify-center">
        <div className="max-w-[80%] rounded-lg bg-gray-50 px-4 py-2 text-center text-sm text-gray-500">
          {message.content}
          <span className="mt-1 block text-xs text-gray-400">
            {formatTime(message.timestamp)}
          </span>
        </div>
      </div>
    )
  }

  // User messages - right aligned, blue background
  if (message.role === 'user') {
    return (
      <div className="mb-4 flex justify-end">
        <div className="max-w-[80%]">
          <div className="rounded-2xl rounded-tr-none bg-blue-500 px-4 py-3 text-white">
            <p className="whitespace-pre-wrap text-sm">{message.content}</p>
          </div>
          <div className="mt-1 text-right">
            <span className="text-xs text-gray-400">
              {formatTime(message.timestamp)}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Assistant messages - left aligned, gray background, with avatar
  return (
    <div className="mb-4 flex items-start gap-2">
      {/* Agent avatar */}
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm">
        <span role="img" aria-label={agentName || 'Agent'}>
          {emoji}
        </span>
      </div>

      <div className="max-w-[80%]">
        {/* Agent name */}
        {agentName && (
          <span className="mb-1 block text-xs font-medium text-gray-600">
            {agentName}
          </span>
        )}

        {/* Message content */}
        <div
          className={`rounded-2xl rounded-tl-none bg-gray-100 px-4 py-3 ${
            message.isStreaming ? 'animate-pulse' : ''
          }`}
        >
          <p className="whitespace-pre-wrap text-sm text-gray-800">
            {message.content}
          </p>
        </div>

        {/* Action buttons */}
        {message.actions && message.actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.actions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => handleActionClick(action)}
                className="
                  inline-flex items-center gap-1 rounded-full border border-blue-200
                  bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700
                  transition-colors hover:bg-blue-100
                "
              >
                {getActionIcon(action.actionType)}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp and token info */}
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {formatTime(message.timestamp)}
          </span>
          {message.tokenCount && (
            <span className="text-xs text-gray-300" title="Tokens used">
              {message.tokenCount.input + message.tokenCount.output} tokens
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Get icon for action type
 */
function getActionIcon(actionType: string): string {
  switch (actionType) {
    case 'insert':
      return '➕'
    case 'replace':
      return '🔄'
    case 'navigate':
      return '📍'
    case 'apply':
      return '✅'
    case 'ignore':
      return '❌'
    default:
      return '•'
  }
}

export default AgentMessageBubble
